import React, { useState } from 'react';
import { useUserHook } from '../../../../hooks/admin/user/use-user';
import {
  Button,
  Dropdown,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
} from 'antd';
import { FaCheckCircle, FaChevronDown, FaEdit, FaEye } from 'react-icons/fa';
import { IoCloseOutline } from 'react-icons/io5';
import { MdBlock } from 'react-icons/md';
import { Ban, Check, Clock, Radio, ScanFace, X } from 'lucide-react';
import { AdminCard, AdminPageHeader, AdminStatusPill } from '../../ui';
import {
  blockCommentUser,
  blockUser,
  confirmCreator,
  deleteUser,
  toggleChangeReciveDonateAPI,
  unBlockCommentUser,
} from '../../../../api/admin/user.api';
import { resetEkycAttempts } from '../../../../api/admin/ekyc.api';
import { promptTotp } from '../../../../utils/admin-stepup';
import toast from 'react-hot-toast';
import { CreateUserModal } from '../../../register/create-form-user';
import CreatorRequestDialog from '../../../user-setting/components/creator-request-dialog';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

const ManagementUser = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [filters, setFilters] = useState<{
    fullName?: string;
    email?: string;
    status?: number;
    isBlockComment?: boolean;
    canReceivedDonation?: boolean;
    isVerifyEmail?: boolean;
  }>({});

  const [sortField, setSortField] = useState<string>('');
  const [isSortDescending, setIsSortDescending] = useState<boolean>(true);
  // Đổi cờ này sau mỗi thao tác (xoá/khoá/duyệt...) để tải lại bảng. Trước đây khai báo SAU hook và không truyền
  // vào nên bảng không cập nhật, phải tải lại trang mới thấy (08/10).
  const [isRefetch, setIsRefetch] = useState(false);
  const { data, total, loading } = useUserHook({
    pageNumber,
    pageSize,
    isRefetch,
    filters,
    sortField,
    isSortDescending,
  });
  const navigator = useNavigate();
  const { Option } = Select;

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    setPageNumber(pagination.current);
    setPageSize(pagination.pageSize);
    setSortField(sorter.field.charAt(0).toUpperCase() + sorter.field.slice(1));
    setIsSortDescending(sorter.order === 'descend');
  };

  const handleFilterChange = (
    key: string,
    value: string | number | undefined | boolean
  ) => {
    setPageNumber(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const [isOpenCreatorRequestDialog, setIsOpenCreatorRequestDialog] =
    useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const handleBlock = async (userId: string) => {
    const totp = promptTotp('khoá/mở tài khoản');
    if (!totp) return;
    try {
      await blockUser(userId, totp);
      setIsRefetch(!isRefetch);
      toast.success('Đã cập nhật trạng thái tài khoản'); // QA S-36
    } catch (error) {
      toast.error('Thao tác thất bại');
    }
  };
  const handleUnBlockComment = async (userId: string) => {
    const totp = promptTotp('mở bình luận');
    if (!totp) return;
    try {
      await unBlockCommentUser(userId, totp);
      setIsRefetch(!isRefetch);
      toast.success('Đã mở bình luận cho người dùng'); // QA S-36: trước báo nhầm "Block user"
    } catch (error) {
      toast.error('Mở bình luận thất bại');
    }
  };
  const handleBlockComment = async (userId: string) => {
    const totp = promptTotp('tắt bình luận');
    if (!totp) return;
    try {
      await blockCommentUser(userId, totp);
      setIsRefetch(!isRefetch);
      toast.success('Đã tắt bình luận của người dùng'); // QA S-36: trước báo nhầm "Block user"
    } catch (error) {
      toast.error('Tắt bình luận thất bại');
    }
  };

  const handleConfirmCreator = async (userId: string) => {
    const totp = promptTotp('duyệt creator');
    if (!totp) return;
    try {
      await confirmCreator(userId, totp);
      setIsRefetch(!isRefetch);
      toast.success('Đã duyệt creator');
    } catch (error: any) {
      // Máy chủ báo lý do (chưa mở nhận donate, cần ghi lý do duyệt ngoại lệ...). Duyệt ngoại lệ làm ở trang Yêu cầu Creator.
      const d = error?.response?.data;
      toast.error(d?.detail || d?.message || 'Duyệt creator thất bại');
    }
  };

  const handleDelete = async (userId: string) => {
    const totp = promptTotp('xoá user');
    if (!totp) return;
    try {
      await deleteUser(userId, totp);
      setIsRefetch(!isRefetch);
      toast.success('Đã xoá người dùng');
    } catch (error) {
      toast.error('Xoá người dùng thất bại');
    }
  };

  const handleToggleChangeReciveDonate = async (id: string) => {
    const totp = promptTotp('bật/tắt nhận donate');
    if (!totp) return;
    try {
      await toggleChangeReciveDonateAPI(id, totp);
      setIsRefetch(!isRefetch);
      toast.success('Đã cập nhật quyền nhận donate');
    } catch (error: any) {
      // Bật nhận donate cho người thiếu điều kiện bắt buộc thì máy chủ trả 400 kèm danh sách mục thiếu.
      const d = error?.response?.data;
      toast.error(d?.detail || d?.message || 'Cập nhật quyền nhận donate thất bại');
    }
  };

  // Mở lại lượt eKYC cho người đã hỏng quá số lần cho phép (máy chủ ghi nhật ký EKYC_RESET_ATTEMPTS).
  const handleResetEkyc = async (userId: string) => {
    const totp = promptTotp('mở lại lượt xác minh danh tính');
    if (!totp) return;
    try {
      await resetEkycAttempts(userId, totp);
      toast.success('Đã mở lại lượt xác minh danh tính.');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Mở lại lượt xác minh danh tính thất bại.');
    }
  };

  const handleSort = (sorter: any) => {
    const { field, order } = sorter;
    setSortField(field.charAt(0).toUpperCase() + field.slice(1));
    setIsSortDescending(order === 'descend');
  };
  const columns = [
    {
      title: 'Tên',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (_, record) => {
        return (
          <div
            className="cursor-pointer adm-link"
            onClick={() => {
              navigator(`/user/${record.userName}`);
            }}
          >
            {record.fullName}
          </div>
        );
      },
    },
    {
      title: 'Email / Tên tài khoản',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      render: (_, record) => {
        return (
          <div>
            <div>{record.email}</div>
            <div className="adm-sub">{record.userName}</div>
          </div>
        );
      },
    },
    {
      title: 'Thời gian đăng kí',
      dataIndex: 'registeredDate',
      key: 'registeredDate',
      sorter: true,
      render: (_, record) => {
        return <div>{dayjs(record.registeredDate).format('DD/MM/YYYY')}</div>;
      },
    },
    {
      title: 'Thời gian online lần cuối',
      dataIndex: 'lastOnlineTimestamp',
      key: 'lastOnlineTimestamp',
      sorter: true,
      render: (_, record) => {
        return (
          <div>
            {record.lastOnlineTimestamp
              ? dayjs(record.lastOnlineTimestamp).format('DD/MM/YYYY HH:mm')
              : 'Chưa có dữ liệu'}
          </div>
        );
      },
    },
    {
      title: 'Xác minh email',
      dataIndex: 'isVerifyEmail',
      key: 'isVerifyEmail',
      sorter: true,
      render: (isVerifyEmail) => {
        return (
          <div>
            {isVerifyEmail ? (
              <AdminStatusPill tone="success">
                <Check size={12} />
                Đã xác minh
              </AdminStatusPill>
            ) : (
              <AdminStatusPill tone="danger">
                <X size={12} />
                Chưa xác minh
              </AdminStatusPill>
            )}
          </div>
        );
      },
    },
    {
      title: 'Yêu cầu Creator',
      dataIndex: 'isRequestCreator',
      key: 'isRequestCreator',
      sorter: true,
      render: (isRequestCreator, record) => {
        return (
          <div>
            {record?.userType === 'NORMAL' && isRequestCreator ? (
              <span
                role="button"
                className="cursor-pointer"
                onClick={() => {
                  setSelectedUserId(record.id);
                  setIsOpenCreatorRequestDialog(true);
                }}
              >
                <AdminStatusPill tone="warning">
                  <Clock size={12} />
                  Đang chờ duyệt
                </AdminStatusPill>
              </span>
            ) : record?.userType === 'CONTENT_CREATOR' ? (
              <AdminStatusPill tone="success">Đã là creator</AdminStatusPill>
            ) : (
              <AdminStatusPill tone="danger">
                <X size={12} />
                Chưa yêu cầu
              </AdminStatusPill>
            )}
          </div>
        );
      },
    },
    {
      title: 'Donate',
      dataIndex: 'canReceiveDonation',
      key: 'canReceiveDonation',
      sorter: true,
      render: (canReceiveDonation) => {
        return (
          <div>
            {canReceiveDonation ? (
              <AdminStatusPill tone="success">
                <Check size={12} />
                Đã kích hoạt
              </AdminStatusPill>
            ) : (
              <AdminStatusPill tone="danger">
                <X size={12} />
                Chưa kích hoạt
              </AdminStatusPill>
            )}
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'isBlock',
      key: 'isBlock',
      sorter: true,
      render: (isBlock) => {
        return (
          <div>
            {!isBlock ? (
              <AdminStatusPill tone="success">
                <Radio size={12} />
                Đang hoạt động
              </AdminStatusPill>
            ) : (
              <AdminStatusPill tone="danger">
                <Ban size={12} />
                Đã khoá
              </AdminStatusPill>
            )}
          </div>
        );
      },
    },

    {
      title: 'Comment',
      dataIndex: 'isBlockComment',
      key: 'isBlockComment',
      sorter: true,
      render: (isBlockComment) => {
        return (
          <div>
            {!isBlockComment ? (
              <AdminStatusPill tone="success">
                <Radio size={12} />
                Đang hoạt động
              </AdminStatusPill>
            ) : (
              <AdminStatusPill tone="danger">
                <Ban size={12} />
                Đã khoá bình luận
              </AdminStatusPill>
            )}
          </div>
        );
      },
    },
    {
      title: 'Action',
      key: 'operation',
      render: (record) => (
        <Space size="middle">
          <Dropdown
            menu={{
              items: [
                {
                  key: '1',
                  label: !record.isBlock ? 'Khoá tài khoản' : 'Mở khoá',
                  icon: <MdBlock className="mr-2" />,
                  danger: true,
                  onClick: () => handleBlock(record.id),
                },
                {
                  key: '2',
                  label:
                    record?.userType === 'NORMAL' && record?.isRequestCreator
                      ? 'Chấp nhận yêu cầu'
                      : ' chưa có yêu cầu',
                  icon: <FaEdit className="mr-2" />,
                  onClick: () =>
                    record?.userType === 'NORMAL' && record?.isRequestCreator
                      ? handleConfirmCreator(record.id)
                      : toast.error('Chưa có yêu cầu'),
                  disabled:
                    record?.userType === 'CONTENT_CREATOR' ||
                    !record?.isRequestCreator,
                },
                {
                  key: '3',
                  label: 'Xoá user',
                  icon: <FaEye className="mr-2" />,
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: 'Bạn có chắc chắn muốn xoá người dùng này?',
                      content: 'Hành động này không thể hoàn tác',
                      okText: 'Xoá',
                      cancelText: 'Huỷ',
                      okButtonProps: { danger: true },
                      onOk: () => handleDelete(record.id),
                    });
                  },
                },
                {
                  key: '4',
                  label: record?.canReceiveDonation
                    ? 'Tắt nhận donate '
                    : 'Bật nhận donate',
                  icon: record?.canReceiveDonation ? (
                    <IoCloseOutline className="mr-2" />
                  ) : (
                    <FaCheckCircle className="mr-2" />
                  ),
                  onClick: () => {
                    Modal.confirm({
                      title: record?.canReceiveDonation
                        ? 'Bạn có chắc chắn muốn tắt nhận donate?'
                        : 'Bạn có chắc chắn muốn bật nhận donate?',
                      content: 'Hành động này có thể ảnh hưởng đến người dùng',
                      okText: 'Xác nhận',
                      cancelText: 'Huỷ',
                      okButtonProps: { danger: true },
                      onOk: () => handleToggleChangeReciveDonate(record.id),
                    });
                  },
                },
                {
                  key: '5',
                  label: !record?.isBlockComment
                    ? 'Tắt bình luận '
                    : 'Bật bình luận',
                  icon: !record?.isBlockComment ? (
                    <IoCloseOutline className="mr-2" />
                  ) : (
                    <FaCheckCircle className="mr-2" />
                  ),
                  onClick: () => {
                    Modal.confirm({
                      title: !record?.isBlockComment
                        ? 'Bạn có chắc chắn muốn tắt bình luận?'
                        : 'Bạn có chắc chắn muốn bật bình luận?',
                      content: 'Hành động này có thể ảnh hưởng đến người dùng',
                      okText: 'Xác nhận',
                      cancelText: 'Huỷ',
                      okButtonProps: { danger: true },
                      onOk: () =>
                        !record?.isBlockComment
                          ? handleUnBlockComment(record.id)
                          : handleBlockComment(record.id),
                    });
                  },
                },
                {
                  key: '6',
                  label: 'Mở lại lượt eKYC',
                  icon: <ScanFace size={14} className="mr-2" />,
                  onClick: () => {
                    Modal.confirm({
                      title: 'Mở lại lượt xác minh danh tính cho tài khoản này?',
                      okText: 'Mở lại',
                      cancelText: 'Huỷ',
                      onOk: () => handleResetEkyc(record.id),
                    });
                  },
                },
              ],
            }}
          >
            <Button>
              <Space>
                Actions
                <FaChevronDown />
              </Space>
            </Button>
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Quản lý người dùng"
        description="Tìm, lọc và xem trạng thái tài khoản người dùng trên Cab."
        actions={
          <div className="adm-create-user [&_button]:!w-auto [&_button]:!rounded-full [&_button]:!bg-[var(--adm-primary)] [&_button]:!text-white [&_button]:!font-semibold [&_button]:!shadow-[var(--adm-shadow-primary)]">
            <CreateUserModal />
          </div>
        }
      />
      <AdminCard title="Bộ lọc tìm kiếm">
        <div
          className="adm-filter-grid"
          style={{
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          }}
        >
          <div className="adm-field">
            <label>Tên</label>
            <Input
              placeholder="Tìm theo tên"
              value={filters.fullName || ''}
              onChange={(e) => handleFilterChange('fullName', e.target.value)}
            />
          </div>
          <div className="adm-field">
            <label>Email</label>
            <Input
              placeholder="Tìm theo email"
              value={filters.email || ''}
              onChange={(e) => handleFilterChange('email', e.target.value)}
            />
          </div>
          <div className="adm-field">
            <label>Xác minh email</label>
            <Select
              placeholder="Chọn trạng thái"
              allowClear
              value={filters.isVerifyEmail}
              onChange={(value) => handleFilterChange('isVerifyEmail', value)}
            >
              <Option value>Đã kích hoạt</Option>
              <Option value={false}>Chưa xác minh</Option>
            </Select>
          </div>
          <div className="adm-field">
            <label>Donate</label>
            <Select
              placeholder="Chọn trạng thái"
              allowClear
              value={filters.canReceivedDonation}
              onChange={(value) =>
                handleFilterChange('canReceivedDonation', value)
              }
            >
              <Option value>Đang hoạt động</Option>
              <Option value={false}>Chưa kích hoạt</Option>
            </Select>
          </div>
          <div className="adm-field">
            <label>Trạng thái</label>
            <Select
              placeholder="Chọn trạng thái"
              allowClear
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value={0}>Đang hoạt động</Option>
              <Option value={1}>Chưa hoạt động</Option>
            </Select>
          </div>
          <div className="adm-field">
            <label>Bình luận</label>
            <Select
              placeholder="Chọn trạng thái"
              allowClear
              value={filters.isBlockComment}
              onChange={(value) => handleFilterChange('isBlockComment', value)}
            >
              <Option value={false}>Đang hoạt động</Option>
              <Option value>Bị khóa</Option>
            </Select>
          </div>
        </div>
      </AdminCard>
      <AdminCard flush>
        <Table
          scroll={{ x: 'max-content' }}
          dataSource={data}
          columns={columns}
          loading={loading}
          pagination={{
            current: pageNumber,
            pageSize,
            total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </AdminCard>
      {/* Creator Request Dialog */}
      {selectedUserId && (
        <CreatorRequestDialog
          userId={selectedUserId}
          isOpen={isOpenCreatorRequestDialog}
          onClose={() => {
            setIsOpenCreatorRequestDialog(false);
            setSelectedUserId(null);
          }}
        />
      )}
    </div>
  );
};

export default ManagementUser;
